// Tagged union Result for predictable outcomes (validation, not-found, etc.).
// Exceptions are reserved for unrecoverable infrastructure failures.

export type Ok<T> = { ok: true; value: T }
export type Err<E> = { ok: false; error: E }
export type Result<T, E> = Ok<T> | Err<E>

export const ok = <T>(value: T): Ok<T> => ({ ok: true, value })
export const err = <E>(error: E): Err<E> => ({ ok: false, error })

/** Narrow an unknown value to a Result by its `ok` discriminant — for callbacks
 *  that may return either a bare value or an already-wrapped Result. */
export const isResult = <T, E = ApiError>(value: unknown): value is Result<T, E> =>
  value !== null && typeof value === "object" && "ok" in value && typeof value.ok === "boolean"

export type ApiErrorKind =
  | "validation"
  | "not_found"
  | "expired"
  | "conflict"
  | "illegal_transition"
  | "unauthorized"
  | "forbidden"
  | "upstream"
  /** This service could not serve the request right now for capacity reasons —
   *  the request itself was fine and retrying later may work. Distinct from
   *  `upstream`, which blames a third party. Maps to 503. Today the only
   *  producer is a query Postgres cancelled at `statement_timeout` (SQLSTATE
   *  57014); the kind is deliberately broader than that one cause and is
   *  reserved for other load-shedding signals. */
  | "unavailable"
  | "network"
  | "aborted"
  | "unknown"

export type ApiError = {
  kind: ApiErrorKind
  message: string
  details?: unknown
  status?: number
}

export const CALLER_AUTH_FAILURE_MARKER = "caller_authentication_failed" as const

export type CallerAuthFailureDetails = {
  marker: typeof CALLER_AUTH_FAILURE_MARKER
}

export function isCallerAuthFailure(error: ApiError): boolean {
  if (
    error.kind !== "unauthorized" ||
    typeof error.details !== "object" ||
    error.details === null
  ) {
    return false
  }
  return "marker" in error.details && error.details.marker === CALLER_AUTH_FAILURE_MARKER
}
